/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { Fragment, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

// Component imports
import { Button, Checkbox, Col, Form, Row, Select, TimePicker, Typography } from 'antd';

// Misc imports
import { useCreateScheduledFlowMutation, useUpdateScheduledFlowMutation } from '../../store/scheduledFlows/scheduledFlowsApi';
import { DAYS_OF_WEEK } from '../../constants/main';
import { getCurrentQueryState } from '../../store/utils/getCurrentQueryState';
import { useFetchActivitiesQuery } from '../../store/activities/activitiesApi';
import { useBotLanguage } from '../../utils/hooks/useBotLanguage';
import { NODE_TYPES } from '../../constants/flowGraph';

const ScheduledFlowForm = ({
  isUpdateMode = false,
  initialSchedule,
  onSubmitSuccess = () => {},
  onCancel = () => {},
  bodyClassName,
  footerClassName,
  ...restProps
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const { tenantId, orgId } = useParams();

  const { data: bot } = getCurrentQueryState('fetchBot', { orgId, tenantId });
  const { data: botDeployments } = getCurrentQueryState('fetchBotDeployments', { orgId, tenantId });
  const { data: activities } = useFetchActivitiesQuery({ orgId, tenantId }, { skip: !orgId || !tenantId });
  const { primaryBotLanguage } = useBotLanguage();

  const [createScheduledFlow, { isLoading: isCreating }] = useCreateScheduledFlowMutation();
  const [updateScheduledFlow, { isLoading: isUpdating }] = useUpdateScheduledFlowMutation();

  const apiDeployments = useMemo(() => {
    if (!botDeployments?.items) {
      return [];
    }
    return botDeployments.items.filter(d => d.platform === 'API' && !d.isPreview);
  }, [botDeployments]);

  const flowOptions = useMemo(() => {
    if (!activities?.items) {
      return [];
    }
    return activities.items.map(activity => ({
      label: activity.name[primaryBotLanguage] || activity.id,
      value: activity.id,
      entryNodeId: (activity?.content?.nodes || []).find(node => node.type === NODE_TYPES.INTRO)?.id
    }));
  }, [activities, primaryBotLanguage]);

  useEffect(() => {
    form.resetFields();

    if (initialSchedule) {
      const matchedFlow = flowOptions.find(f => f.entryNodeId === initialSchedule.flowNodeId);

      form.setFieldsValue({
        botDeploymentId: initialSchedule.botDeploymentId,
        flowNodeId: matchedFlow?.value || initialSchedule.flowNodeId,
        daysOfWeek: initialSchedule.daysOfWeek || [],
        timeOfDay: initialSchedule.timeOfDay ? moment(initialSchedule.timeOfDay, 'HH:mm') : null
      });
    }

    return () => {
      form.resetFields();
    };
  }, [initialSchedule, flowOptions]);

  const onSubmit = async () => {
    const values = await form.validateFields();

    const selectedDeployment = apiDeployments.find(d => d.id === values.botDeploymentId);
    const selectedFlow = flowOptions.find(f => f.value === values.flowNodeId);

    const payload = {
      orgId,
      tenantId,
      botDeploymentId: values.botDeploymentId,
      deploymentName: selectedDeployment?.displayName || selectedDeployment?.name || '',
      flowNodeId: selectedFlow?.entryNodeId || values.flowNodeId,
      flowName: selectedFlow?.label || '',
      daysOfWeek: values.daysOfWeek,
      timeOfDay: values.timeOfDay ? values.timeOfDay.format('HH:mm') : '',
      timezone: bot?.timezone || 'UTC'
    };

    let success = false;

    if (isUpdateMode && initialSchedule) {
      const result = await updateScheduledFlow({ id: initialSchedule.id, ...payload });
      success = !result.error;
    } else {
      const result = await createScheduledFlow(payload);
      success = !result.error;
    }

    if (success) {
      onSubmitSuccess();
    }
  };

  return (
    <Fragment>
      <div className={bodyClassName}>
        <Form
          onFinish={onSubmit}
          name="scheduledFlowForm"
          layout="horizontal"
          form={form}
          initialValues={{
            daysOfWeek: [],
            botDeploymentId: undefined,
            flowNodeId: undefined,
            timeOfDay: null
          }}
          labelCol={{ span: 6 }}
          wrapperCol={{ span: 18 }}
          labelAlign="left"
          {...restProps}
        >
          <Form.Item
            label={t('Deployment')}
            name="botDeploymentId"
            rules={[{
              required: true,
              message: t('Please select a deployment.')
            }]}
          >
            <Select
              placeholder={t('Select an API deployment')}
              allowClear
              getPopupContainer={triggerNode => triggerNode.parentElement}
            >
              {apiDeployments.map((deployment) => (
                <Select.Option key={deployment.id} value={deployment.id}>
                  {deployment.displayName || deployment.name || deployment.id}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label={t('Flow')}
            name="flowNodeId"
            rules={[{
              required: true,
              message: t('Please select a flow.')
            }]}
          >
            <Select
              placeholder={t('Select a flow')}
              allowClear
              showSearch
              optionFilterProp="children"
              getPopupContainer={triggerNode => triggerNode.parentElement}
            >
              {flowOptions.map((flow) => (
                <Select.Option key={flow.value} value={flow.value}>
                  {flow.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label={t('Days')}
            name="daysOfWeek"
            rules={[{
              validator: (_, value) => value && value.length > 0
                ? Promise.resolve()
                : Promise.reject(new Error(t('Please select at least one day.')))
            }]}
          >
            <Checkbox.Group options={DAYS_OF_WEEK} />
          </Form.Item>

          <Form.Item
            label={t('Time')}
            name="timeOfDay"
            rules={[{
              required: true,
              message: t('Please select a time.')
            }]}
          >
            <TimePicker
              format="HH:mm"
              minuteStep={5}
              getPopupContainer={triggerNode => triggerNode.parentElement}
            />
          </Form.Item>

          <Form.Item
            label={t('Timezone')}
          >
            <Typography.Text type="secondary">
              {bot?.timezone || 'UTC'}
            </Typography.Text>
          </Form.Item>
        </Form>
      </div>

      <div className={footerClassName}>
        <Row justify="end" gutter={8}>
          <Col>
            <Button
              size="large"
              onClick={onCancel}
              disabled={isCreating || isUpdating}
            >
              {t('Cancel')}
            </Button>
          </Col>

          <Col>
            <Button
              type="primary"
              onClick={() => form.submit()}
              loading={isCreating || isUpdating}
              size="large"
            >
              {isUpdateMode ? t('Save Changes') : t('Create Schedule')}
            </Button>
          </Col>
        </Row>
      </div>
    </Fragment>
  );
};

ScheduledFlowForm.propTypes = {
  isUpdateMode: PropTypes.bool,
  initialSchedule: PropTypes.object,
  onSubmitSuccess: PropTypes.func,
  onCancel: PropTypes.func,
  bodyClassName: PropTypes.string,
  footerClassName: PropTypes.string
};

export default ScheduledFlowForm;
